import { useIntl } from "react-intl";
import {
  Modal,
  Box,
  IconButton,
  Button,
  Typography,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useLoading } from "../LoadingContext";
import axios from "../../api/axiosClient";
import {
  handleError,
  errorToast,
  addDownloadLinkToDocument,
} from "../../services/serviceBase";
import CustomGridHeaderCell from "../ui/CustomGridHeaderCell";
import CustomTable from "../ui/CustomTable";
import CustomTablePagingInfo from "../ui/CustomTablePagingInfo";
import Checkbox from "@mui/material/Checkbox";
import CustomInput from "../ui/CustomInput";
import PageForm from "../ui/PageForm";
import { use } from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { setSearch } from "../../redux/searchSlice";

const DibaEditModal = (props) => {
  const intl = useIntl();

  const modalStyle = {
    position: "relative",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 3,
    borderRadius: 2,
  };

  const { setLoading } = useLoading();

  const [dibaData, setDibaData] = useState([]);
  const [columns, setColumns] = useState([]);

  const [totalCount, setTotalCount] = useState(0);
  const page = useRef(0);
  const maxPage = useRef(0);
  const rowsForPage = 6;

  const editedRows = useRef({});
  const [rowErrors, setRowErrors] = useState({});

  const [tableHeigth, setTableHeigth] = useState(20);
  const [shouldFilterCollapse, setShouldFilterCollapse] = useState(false);

  const [merchClass, setMerchClass] = useState("");
  const [merchSubclass, setMerchSubclass] = useState("");
  const [suffix, setSuffix] = useState("");
  const [part, setPart] = useState("");
  const [color, setColor] = useState("");
  const [description, setDescription] = useState("");

  const getDibaData = async () => {
    setLoading(true);
    const params = new URLSearchParams([]);

    params.append("Class", merchClass);
    params.append("SubClass", merchSubclass);
    params.append("Suffix", suffix);
    params.append("Part", part);
    params.append("Color", color);
    params.append("Description", description);

    if (page.current === 0) {
      page.current = 1;
    }
    params.append("PagingInfo.Skip", `${rowsForPage * (page.current - 1)}`);
    params.append("PagingInfo.Take", `${rowsForPage}`);

    axios
      .get("/diba/qualitycheck/" + props.idQualityCheck, { params })
      .then((response) => {
        setLoading(false);
        const mergedData = response.data.paginatedItems.map(
          (item) => editedRows.current[item.idDibaItem] || item
        );
        setDibaData(mergedData);
        setTotalCount(response.data.totalCount);
        maxPage.current = Math.ceil(response.data.totalCount / rowsForPage);
      })
      .catch((error) => {
        setLoading(false);
        handleError(error, intl);
      });
  };

  useEffect(() => {
    getColumns();
  }, [dibaData]);

  const handleCopyConsumption = (row) => {
    let newDataset = [...dibaData];
    var item = newDataset.find((x) => x.idDibaItem == row.idDibaItem);
    if (item) {
      item.quantity = item.consumption;
      item.check = item.consumption > 0;
      setDibaData(newDataset);
      editedRows.current[item.idDibaItem] = { ...item };
    }
  };

  const getColumns = () => {
    setColumns([
      {
        field: "merchClass",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.MerchClass" })}
          />
        ),
        flex: 1,
      },
      {
        field: "merchSubClass",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.MerchSubClass" })}
          />
        ),
        flex: 1,
      },
      {
        field: "fabricSuffix",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Suffix" })}
          />
        ),
        flex: 1,
      },
      {
        field: "fabricCode",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Part" })}
          />
        ),
        flex: 1,
      },
      {
        field: "colorCode",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Color" })}
          />
        ),
        flex: 1,
      },
      {
        field: "styleFabricColorDescription",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Description" })}
          />
        ),
        flex: 1,
        minWidth: 500,
      },
      {
        field: "consumption",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Consumption" })}
          />
        ),
        flex: 1,
        renderCell: (props) => (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ height: "100%" }}>
            <Typography>{props.value}</Typography>
            <Typography>
              {props.row.dibaUnitOfMeasure}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "action",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => ( <CustomGridHeaderCell/> ),
        width: 100,
        renderCell: (props) => (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ height: "100%" }}>
            <IconButton
              onClick={() => handleCopyConsumption(props.row)}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Stack>
        ),
      },
      {
        field: "quantity",
        headerClassName: "grid-header apeLigthBlueBackground",
        renderHeader: () => (
          <CustomGridHeaderCell
            item1={intl.formatMessage({ id: "DibaEdit.Qty" })}
          />
        ),
        flex: 1,
        renderCell: RenderNumericInput,
      },
    ]);
  };

  function RenderCheckBox(props) {
    const [checked, setChecked] = useState(props.value);

    useEffect(() => {
      setChecked(props.value);
    }, [props.value]);

    const handleChange = (event) => {
      const newChecked = event.target.checked;
      setChecked(newChecked);
      let newDataset = [...dibaData];
      var item = newDataset.find((x) => x.idDibaItem == props.row.idDibaItem);
      if (item) {
        item[props.field] = newChecked;
        if (props.onCheckedChange) {
          props.onCheckedChange(item, newChecked);
        }
        setDibaData(newDataset);
      }
    };

    return <Checkbox checked={checked} onChange={handleChange} />;
  }

  function RenderNumericInput(props) {
    const [value, setValue] = useState(props.value);
    const [error, setError] = useState(false);

    useEffect(() => {
      setValue(props.value);
      const maxValue = props.row.consumption;
      const isExceeded =
        props.value !== "" &&
        props.value !== null &&
        Number(props.value) > Number(maxValue);
      setError(isExceeded);
    }, [props.value]);

    const handleChange = (event) => {
      if (event != undefined) {
        const newValue = event;
        const maxValue = props.row.consumption;
        const isExceeded =
          newValue !== "" &&
          newValue !== null &&
          Number(newValue) > Number(maxValue);

        setError(isExceeded);
        setValue(newValue);

        setRowErrors((prev) => {
          const next = { ...prev };
          if (isExceeded) {
            next[props.row.idDibaItem] = true;
          } else {
            delete next[props.row.idDibaItem];
          }
          return next;
        });

        let newDataset = [...dibaData];
        var item = newDataset.find((x) => x.idDibaItem == props.row.idDibaItem);
        if (item) {
          item[props.field] = newValue;
          item.check = newValue > 0;
          setDibaData(newDataset);
          editedRows.current[item.idDibaItem] = { ...item };
        }
      }
    };

    return (
      <CustomInput
        type="number"
        value={value}
        setValue={(e) => {
          handleChange(e);
        }}
        inputCol={12}
        error={error}
        helperText={error ? `Max: ${props.row.consumption}` : ""}
        min={0}
      />
    );
  }

  useEffect(() => {
    if (
      props.idQualityCheck !== null &&
      props.idQualityCheck !== undefined &&
      props.idQualityCheck !== "" &&
      props.openModal
    ) {
      editedRows.current = {};
      setRowErrors({});
      setShouldFilterCollapse(false);
      page.current = 0;
      getDibaData();
    }
  }, [props.openModal]);

  const handleClose = () => {
    props.handleCloseModal();
    setDibaData([]);
    editedRows.current = {};
    setRowErrors({});
  };

  const handleSave = () => {
    if (!props.idQualityCheck) return;

    setLoading(true);
    const allEdits = { ...editedRows.current };
    dibaData.forEach((item) => {
      allEdits[item.idDibaItem] = { ...item };
    });
    //const payload = Object.values(allEdits).filter((x) => x.quantity > 0);
    const payload = Object.values(allEdits).filter(
      (x) => x.quantity !== "" && x.quantity !== null && Number(x.quantity) > 0
    );
    axios
      .post(`/diba/${props.idQualityCheck}`, payload)
      .then(() => {
        setLoading(false);
        props.handleCloseModal();
        setDibaData([]);
        editedRows.current = {};
        setRowErrors({});
      })
      .catch((error) => {
        setLoading(false);
        handleError(error, intl);
      });
  };

  const handleNextPage = () => {
    if (page.current < maxPage.current) {
      page.current = page.current + 1;
      getDibaData();
    }
  };

  const handlePreviousPage = () => {
    if (page.current > 1) {
      page.current = page.current - 1;
      getDibaData();
    }
  };

  const handletrasitionEnd = () => {
    resizeTable();
  };

  const resizeTable = () => {
    var fromEl = document.querySelector("#form");
    var headerEl = document.querySelector("#header");
    var pagingInfoEl = document.querySelector("#pagingInfo");
    var fromHeight = fromEl?.clientHeight ?? 0;
    var headerHeight = headerEl?.clientHeight ?? 0;
    var pagingInfoHeight = pagingInfoEl?.clientHeight ?? 0;
    var pageHeight = window.innerHeight;
    var tableHeight =
      pageHeight - fromHeight - headerHeight - pagingInfoHeight - 90;
    setTableHeigth(tableHeight);
  };

  const handleResetFilters = () => {
    setMerchClass("");
    setMerchSubclass("");
    setSuffix("");
    setPart("");
    setColor("");
    setDescription("");
  };

  return (
    <Modal open={props.openModal} onClose={handleClose}>
      <Box sx={modalStyle}>
        <Grid container>
          <Grid
            item
            lg={12}
            sx={{ background: "#bcb7b7", color: "white", p: 0.5 }}
          >
            <Stack direction="row" spacing={3}>
              <Typography sx={{ fontSize: 18 }}>
                {intl.formatMessage({ id: "DibaEdit.DibaSku" })}:{" "}
                {props.article.barcode + props.article.checkDigit}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3}>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Model" })}:{" "}
                {props.article.styleCode}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Part" })}:{" "}
                {props.article.fabricCode}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Color" })}:{" "}
                {props.article.colorCode}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Size" })}:{" "}
                {props.article.size}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Drop" })}:{" "}
                {props.article.drop}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.Variant" })}:{" "}
                {props.article.variant}
              </Typography>
              <Typography>
                {intl.formatMessage({ id: "DibaEdit.VariantType" })}:{" "}
                {props.article.variantType}
              </Typography>
            </Stack>
          </Grid>
        </Grid>

        {dibaData !== null && dibaData != [] ? (
          <>
            <PageForm
              adjustHeightFunc={handletrasitionEnd}
              shouldCollapse={shouldFilterCollapse}
              form={
                <form>
                  <Grid container spacing={2}>
                    <Grid item lg={12}>
                      <Grid container spacing={2}>
                        <Grid item lg={3}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.MerchClass",
                            })}
                            type="text"
                            value={merchClass}
                            setValue={setMerchClass}
                            textCol={4}
                            inputCol={8}
                          />
                        </Grid>
                        <Grid item lg={3}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.MerchSubClass",
                            })}
                            type="text"
                            value={merchSubclass}
                            setValue={setMerchSubclass}
                            textCol={4}
                            inputCol={8}
                          />
                        </Grid>
                        <Grid item lg={2}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.Suffix",
                            })}
                            type="text"
                            value={suffix}
                            setValue={setSuffix}
                            textCol={4}
                            inputCol={8}
                          />
                        </Grid>
                        <Grid item lg={2}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.Part",
                            })}
                            type="text"
                            value={part}
                            setValue={setPart}
                            textCol={4}
                            inputCol={8}
                          />
                        </Grid>
                        <Grid item lg={2}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.Color",
                            })}
                            type="text"
                            value={color}
                            setValue={setColor}
                            textCol={4}
                            inputCol={8}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={12}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item lg={8}>
                          <CustomInput
                            labelText={intl.formatMessage({
                              id: "DibaEdit.Description",
                            })}
                            type="text"
                            value={description}
                            setValue={setDescription}
                            textCol={2}
                            inputCol={10}
                          />
                        </Grid>
                        <Grid item lg={2}>
                          <Button
                            variant="contained"
                            sx={{ width: "100%" }}
                            className="apeLigthBlueBackground"
                            onClick={handleResetFilters}
                          >
                            {intl.formatMessage({ id: "General.Reset" })}
                          </Button>
                        </Grid>
                        <Grid item lg={2}>
                          <Button
                            variant="contained"
                            sx={{ width: "100%" }}
                            className="apeLigthBlueBackground"
                            onClick={() => {
                              page.current = 0;
                              setShouldFilterCollapse(false);
                              setTimeout(() => {
                                setShouldFilterCollapse(true);
                              }, 0);

                              getDibaData();
                            }}
                          >
                            {intl.formatMessage({ id: "General.Search" })}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </form>
              }
            />
            <CustomTablePagingInfo
              totalCount={totalCount}
              page={page.current}
              maxPage={maxPage.current}
              handlePreviousPage={handlePreviousPage}
              handleNextPage={handleNextPage}
            />
            <CustomTable
              columns={columns}
              rows={dibaData}
              id={"idDibaItem"}
              rowHeight={55}
              headerHeight={50}
              tableHeight={420}
            />
          </>
        ) : (
          ""
        )}
        <Grid container mt={2}>
          <Grid item lg={2}>
            <Button
              className="QcBlueButton"
              variant="outlined"
              onClick={handleClose}
              fullWidth
            >
              {intl.formatMessage({ id: "DibaEdit.Close" })}
            </Button>
          </Grid>
          <Grid item lg={8}></Grid>
          <Grid item lg={2}>
            <Button
              className="QcBlueButton"
              variant="outlined"
              onClick={handleSave}
              fullWidth
              disabled={props.disableSaving || Object.keys(rowErrors).length > 0}
            >
              {intl.formatMessage({ id: "DibaEdit.Save" })}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default DibaEditModal;
